import React, { useState, useEffect } from 'react';
import { ResponsiveLine } from '@nivo/line'

const APCA_API_KEY_ID = process.env.REACT_APP_APCA_API_KEY_ID;
const APCA_API_SECRET_KEY = process.env.REACT_APP_APCA_API_SECRET_KEY;

// Generate a random color in hex format
const generateRandomColor = () => {
    const hue = Math.floor(Math.random() * 360);
    const saturation = 60 + Math.floor(Math.random() * 30); // 60-90%
    const lightness = 40 + Math.floor(Math.random() * 20); // 40-60%
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

// Get stored tickers and colors from localStorage
const getStoredTickers = () => {
    const stored = localStorage.getItem('stockTickers');
    return stored ? JSON.parse(stored) : [];
};

const getStoredColors = () => {
    const stored = localStorage.getItem('tickerColors');
    return stored ? JSON.parse(stored) : {};
};

// Generate colors for tickers, reusing existing colors where possible
const generateColorsForTickers = (tickers, existingColors = {}) => {
    const colors = { ...existingColors };
    for (const ticker of tickers) {
        if (!colors[ticker]) {
            colors[ticker] = generateRandomColor();
        }
    }
    return colors;
};

async function fetchAssets() {
    const headers = {
        "accept": "application/json",
        "APCA-API-KEY-ID": APCA_API_KEY_ID,
        "APCA-API-SECRET-KEY": APCA_API_SECRET_KEY
    };
    try {
        const response = await fetch('https://paper-api.alpaca.markets/v2/assets?attributes=&status=active', {
             method: 'GET',
             headers: headers
        });
        if (!response.ok) throw new Error(response.statusText);
        return await response.json();
    } catch (e) {
        console.error("Failed to fetch assets", e);
        return [];
    }
}
  
async function fetchData(tickers, colors) {
    if (tickers.length === 0) {
        return [];
    }

    const headers = {
        "accept": "application/json",
        "APCA-API-KEY-ID": APCA_API_KEY_ID,
        "APCA-API-SECRET-KEY": APCA_API_SECRET_KEY
    };

    const currentDate = new Date();
    const today = new Date(currentDate.getTime() - currentDate.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 19) + "Z";

    const urlCalendar = `https://paper-api.alpaca.markets/v2/calendar?end=${today}`;

    const responseCalendar = await fetch(urlCalendar, { headers });
    const calendarData = await responseCalendar.json();
    const todayDate = calendarData[calendarData.length - 1]["date"];
    const yesterdayDate = calendarData[calendarData.length - 2]["date"];
    const marketClose = `${yesterdayDate}T21:00:00Z`;

    let nextPageToken = null;

    let barsData = {"bars": {}, "next_page_token": null};

    do {
        const urlBars = `https://data.alpaca.markets/v2/stocks/bars?symbols=${tickers.join('%2C')}&timeframe=5Min&start=${marketClose}&limit=10000&adjustment=raw&feed=sip&sort=asc${nextPageToken ? `&page_token=${nextPageToken}` : ''}`;
        const responseBars = await fetch(urlBars, { headers });
        const newData = await responseBars.json();
        
        if (!newData.bars) {
            break;
        }
        
        for (const ticker of Object.keys(newData.bars)) {
            if (Object.keys(barsData.bars).includes(ticker)) {
                barsData.bars[ticker] = [...barsData.bars[ticker], ...newData.bars[ticker]];
                delete newData.bars.ticker;
            } else {
                barsData.bars[ticker] = newData.bars[ticker];
            }
        }
        
        nextPageToken = newData["next_page_token"];
    } while (nextPageToken);

    const data = [];
    for (const [ticker, stockData] of Object.entries(barsData["bars"])) {
        const prevClose = stockData[0]["o"];
        const dataParsed = [];

        for (const item of stockData) {

            if (item["t"].slice(0, 10) !== todayDate) {
                continue;
            }

            const datetimeObj = new Date(item["t"]);
            const pacificTime = datetimeObj.toLocaleString("en-US", { timeZone: "America/Los_Angeles" });
            const pacificDate = new Date(pacificTime);

            if ((pacificDate.getHours() >= 7 && pacificDate.getHours() <= 12) ||
                (pacificDate.getHours() === 6 && pacificDate.getMinutes() >= 30) ||
                (pacificDate.getHours() === 13 && pacificDate.getMinutes() === 0)) {
                const timestamp = pacificDate;
                let percentChange = ((item["o"] - prevClose) / prevClose) * 100;
                dataParsed.push({
                    "x": timestamp.getHours() + ":" + timestamp.getMinutes().toString().padStart(2, '0'),
                    "y": percentChange
                });
            }
        }

        data.push({
            "id": ticker,
            "color": colors[ticker],
            "data": dataParsed
        });
    }
    
    return data;
}

const CustomTooltip = ({ point }) => (
    <div className="custom-tooltip">
        <div className="tooltip-header">{point.serieId}</div>
        <div className="tooltip-row">
            <span>Time:</span>
            <span className="tooltip-value">{point.data.xFormatted || point.data.x}</span>
        </div>
        <div className="tooltip-row">
            <span>Change:</span>
            <span className="tooltip-value">{point.data.yFormatted || point.data.y}%</span>
        </div>
    </div>
);

const Chart = () => {
    const [allAssets, setAllAssets] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [tickers, setTickers] = useState(getStoredTickers);
    const [colors, setColors] = useState(getStoredColors);
    const [tickerInput, setTickerInput] = useState('');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchAssets().then(setAllAssets);
    }, []);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setTickerInput(value);
        if (value.length > 0) {
            const filtered = allAssets.filter(asset => 
                (asset.symbol && asset.symbol.toLowerCase().startsWith(value.toLowerCase())) ||
                (asset.name && asset.name.toLowerCase().startsWith(value.toLowerCase()))
            ).slice(0, 10);
            setSuggestions(filtered);
        } else {
            setSuggestions([]);
        }
    };

    const selectSuggestion = (asset) => {
        const newTicker = asset.symbol;
        if (!tickers.includes(newTicker)) {
            const updatedTickers = [...tickers, newTicker];
            setTickers(updatedTickers);
            localStorage.setItem('stockTickers', JSON.stringify(updatedTickers));
        }
        setTickerInput('');
        setSuggestions([]);
    };

    // Load data when tickers change
    useEffect(() => {
        const loadData = async () => {
            if (tickers.length === 0) {
                setData([]);
                return;
            }
            setLoading(true);
            try {
                setColors(prevColors => {
                    const newColors = generateColorsForTickers(tickers, prevColors);
                    localStorage.setItem('tickerColors', JSON.stringify(newColors));
                    
                    // Fetch data with the new colors
                    fetchData(tickers, newColors).then(chartData => {
                        setData(chartData);
                        setLoading(false);
                    }).catch(error => {
                        console.error('Error fetching data:', error);
                        setLoading(false);
                    });
                    
                    return newColors;
                });
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoading(false);
            }
        };
        loadData();
    }, [tickers]);

    const handleAddTickers = () => {
        // Parse input - split by commas, spaces, or newlines
        const newTickers = tickerInput
            .toUpperCase()
            .split(/[,\s\n]+/)
            .map(t => t.trim())
            .filter(t => t.length > 0 && !tickers.includes(t));
        
        if (newTickers.length > 0) {
            const updatedTickers = [...tickers, ...newTickers];
            setTickers(updatedTickers);
            localStorage.setItem('stockTickers', JSON.stringify(updatedTickers));
        }
        setTickerInput('');
    };

    const handleRemoveTicker = (tickerToRemove) => {
        const updatedTickers = tickers.filter(t => t !== tickerToRemove);
        setTickers(updatedTickers);
        localStorage.setItem('stockTickers', JSON.stringify(updatedTickers));
    };

    const handleClearAll = () => {
        setTickers([]);
        setColors({});
        localStorage.removeItem('stockTickers');
        localStorage.removeItem('tickerColors');
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAddTickers();
        }
    };

    return (<>
        <div className="controls-card">
            <div className="input-group">
                <div style={{position: 'relative', flexGrow: 1}}>
                    <input
                        className="ticker-input"
                        type="text"
                        value={tickerInput}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPress}
                        placeholder="Search tickers"
                        style={{width: '100%', boxSizing: 'border-box'}}
                    />
                     {suggestions.length > 0 && (
                        <ul className="suggestions-list">
                            {suggestions.map(s => (
                                <li key={s.id} onClick={() => selectSuggestion(s)}>
                                    {s.symbol} - {s.name} 
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <button 
                    className="btn btn-primary"
                    onClick={handleAddTickers}
                >
                    Add Tickers
                </button>
                <button 
                    className="btn btn-secondary"
                    onClick={handleClearAll}
                >
                    Clear All
                </button>
            </div>
            <div className="ticker-list">
                {tickers.map(ticker => (
                    <span 
                        key={ticker}
                        className="ticker-chip"
                        style={{
                            backgroundColor: colors[ticker] || '#ccc',
                        }}
                    >
                        {ticker}
                        <button
                            className="ticker-remove-btn"
                            onClick={() => handleRemoveTicker(ticker)}
                        >
                            ×
                        </button>
                    </span>
                ))}
            </div>
        </div>

        <div className="chart-card">
            {loading && (
                <div className="loading-overlay">
                    <div className="spinner"></div>
                    <span>Loading Data...</span>
                </div>
            )}
            
            <div className="chart-header">
                <div className="chart-title-group">
                    <h2 className="chart-title">Intraday Performance</h2>
                    <div className="chart-subtitle">Percent change from yesterday's close</div>
                </div>
                {/* {active && <span className="chart-status">Active: {active}</span>} */}
            </div>

            <div className="chart-wrapper">
                {data.length > 0 ? (
                <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}>
                    <ResponsiveLine
                        data={data}
                        theme={{
                            axis: {
                                ticks: {
                                    text: { fill: '#64748b' }
                                },
                                legend: {
                                    text: { fill: '#1e293b', fontWeight: 600 }
                                }
                            },
                            grid: {
                                line: { stroke: '#e2e8f0', strokeWidth: 1 }
                            },
                            crosshair: {
                                line: {
                                    stroke: '#64748b',
                                    strokeWidth: 1,
                                    strokeOpacity: 0.5,
                                },
                            },
                        }}
                        markers={[
                            {
                                axis: 'y',
                                value: 0,
                                lineStyle: { stroke: '#000000', strokeWidth: 2, strokeOpacity: 0.5 },
                            },
                        ]}
                        margin={{ top: 20, right: 130, bottom: 50, left: 60 }}
                        xScale={{
                            type: 'point',
                        }}
                        yScale={{
                            type: 'linear',
                            min: 'auto',
                            max: 'auto',
                            stacked: false,
                            reverse: false
                        }}
                        yFormat=" >-.2f"
                        axisTop={null}
                        axisRight={null}
                        axisBottom={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: 'Time',
                            legendOffset: 36,
                            legendPosition: 'middle',
                            truncateTickAt: 0,
                            tickValues: ["7:00", "8:00", "9:00", "10:00", "11:00", "12:00", "13:00"]
                        }}
                        axisLeft={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: 'Percent Change',
                            legendOffset: -40,
                            legendPosition: 'middle',
                            truncateTickAt: 0,
                        }}
                        pointSize={2}
                        pointBorderWidth={2}
                        pointLabel="data.yFormatted"
                        pointLabelYOffset={-12}
                        enableTouchCrosshair={true}
                        useMesh={true}
                        enableCrosshair={true}
                        colors={d => d.color}
                        legends={[
                            {
                                anchor: 'bottom-right',
                                direction: 'column',
                                justify: false,
                                translateX: 120,
                                translateY: 0,
                                itemsSpacing: 0,
                                itemDirection: 'left-to-right',
                                itemWidth: 80,
                                itemHeight: 20,
                                itemOpacity: 0.75,
                                symbolSize: 12,
                                symbolShape: 'circle',
                                symbolBorderColor: 'rgba(0, 0, 0, .5)',
                                effects: [
                                    {
                                        on: 'hover',
                                        style: {
                                            itemBackground: 'rgba(0, 0, 0, .03)',
                                            itemOpacity: 1
                                        }
                                    }
                                ],
                            }
                        ]}
                        tooltip={CustomTooltip}
                        animate={false}
                    />
                </div>
                ) : (
                    !loading && (
                        <div className="empty-state">
                                 <div className="empty-state-icon">
                                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="20" x2="12" y2="10"></line>
                                        <line x1="18" y1="20" x2="18" y2="4"></line>
                                        <line x1="6" y1="20" x2="6" y2="16"></line>
                                    </svg>
                                </div>
                            <p>Add tickers to visualize daily stock performance</p>
                        </div>
                    )
                )}
            </div>
        </div>
    </>
    )
}

export default Chart;
